import lodash from 'lodash';

import {
  ITEM_OUTCOME_HIDE_ACTION,
  ITEM_OUTCOME_OVERRIDE_DELETE,
  ITEM_OUTCOME_OVERRIDE_UPDATE,
  ITEM_OUTCOME_UNHIDE_ACTION,
} from 'bundles/author-assignment-grading/constants/ItemOutcomeActions';
import { getDecimalGradeFromPercent } from 'bundles/author-common/utils/GradeUtils';
import type { ConflictMetadata, ItemOutcomeAction, UserItemGrade } from 'bundles/authoring/common/types/gradebookV2';

export const getGradeActionByTypeName = (grade: UserItemGrade | null, typeName: string) => {
  return grade && grade.itemOutcomeActions && grade.itemOutcomeActions.find((action) => action.typeName === typeName);
};

export const hasGradeActionsByTypeName = (grade: UserItemGrade | null, typeNames: Array<string>) =>
  typeNames.reduce((has, typeName) => has || !!getGradeActionByTypeName(grade, typeName), false);

export const normalizeActions = (actions: Array<ItemOutcomeAction>) => {
  const normalizedActions: Array<ItemOutcomeAction> = actions.map((action) => {
    if (action.typeName === ITEM_OUTCOME_OVERRIDE_UPDATE && action.definition.grade > 0) {
      const definition = { ...action.definition, grade: getDecimalGradeFromPercent(action.definition.grade) };
      return { typeName: ITEM_OUTCOME_OVERRIDE_UPDATE, definition };
    }
    return action;
  });

  return normalizedActions;
};

export const getUserGradebookForItemOutcomeAction = (
  grade: UserItemGrade,
  action: ItemOutcomeAction,
  itemId: string
) => {
  const { conflictMetadata } = grade;
  const originalActions = grade.itemOutcomeActions || [];
  const { typeName } = action;

  let actionsToRemove: any[] = [];
  let addNewAction = true;

  switch (typeName) {
    case ITEM_OUTCOME_OVERRIDE_UPDATE:
      actionsToRemove = [ITEM_OUTCOME_OVERRIDE_UPDATE, ITEM_OUTCOME_OVERRIDE_DELETE];
      break;
    case ITEM_OUTCOME_OVERRIDE_DELETE:
      actionsToRemove = [ITEM_OUTCOME_OVERRIDE_UPDATE, ITEM_OUTCOME_OVERRIDE_DELETE];
      if (hasGradeActionsByTypeName(grade, [ITEM_OUTCOME_OVERRIDE_UPDATE, ITEM_OUTCOME_OVERRIDE_DELETE])) {
        addNewAction = false;
      }
      break;
    case ITEM_OUTCOME_HIDE_ACTION:
      actionsToRemove = [ITEM_OUTCOME_HIDE_ACTION, ITEM_OUTCOME_UNHIDE_ACTION];
      if (grade.isHidden) {
        addNewAction = false;
      }
      break;
    case ITEM_OUTCOME_UNHIDE_ACTION: {
      actionsToRemove = [ITEM_OUTCOME_HIDE_ACTION, ITEM_OUTCOME_UNHIDE_ACTION];
      if (!grade.isHidden) {
        addNewAction = false;
      }
      break;
    }
    default:
      throw new Error('ItemOutcomeAction with unknown typeName found');
  }

  const itemOutcomeAction = normalizeActions([
    ...originalActions.filter((itemAction) => !actionsToRemove.includes(itemAction.typeName)),
    ...(addNewAction ? [action] : []),
  ]);

  return {
    itemOutcomeActions: {
      [itemId]: {
        itemOutcomeAction,
        conflictMetadata,
      },
    },
  };
};

type UserGradebooks = {
  [userId: string]: {
    itemOutcomeActions: {
      [itemId: string]: {
        itemOutcomeAction: Array<ItemOutcomeAction>;
        conflictMetadata: ConflictMetadata;
      };
    };
  };
};

export const getUserGradebooksHaveAnActionForItem = (userGradebooks: UserGradebooks, itemId: string): boolean => {
  return lodash
    .values(userGradebooks)
    .reduce<boolean>(
      (hasAction, gradebook) => hasAction || gradebook.itemOutcomeActions[itemId].itemOutcomeAction.length > 0,
      false
    );
};

export const getUserGradebookReleasesForGrade = (grade: UserItemGrade, itemId: string) => {
  return { itemOutcomeActionsRelease: { [itemId]: { conflictMetadata: grade.conflictMetadata } } };
};

export default getUserGradebookForItemOutcomeAction;
